import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Card, Button, Typography, Steps, Form, Input, Result, Spin, message, Space, Tag, Alert, Row, Col, Divider,
} from 'antd';
import {
  ShoppingCartOutlined, QrcodeOutlined, CheckCircleOutlined,
  ClockCircleOutlined, LoadingOutlined,
} from '@ant-design/icons';
import { useSearchParams, useNavigate } from 'react-router-dom';
import QRCode from 'qrcode';
import { useLanguage } from '../store/LanguageContext';
import { orderAPI, paymentAPI } from '../services/api';
import { Order, PaymentMethodItem } from '../types';

const { Title, Text } = Typography;

type CheckoutStep = 'form' | 'pay' | 'done';

const CheckoutPage: React.FC = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const planCode = searchParams.get('plan') || 'standard';
  const toolIdParam = searchParams.get('tool_id');
  const toolNameParam = searchParams.get('tool_name');

  const [step, setStep] = useState<CheckoutStep>('form');
  const [loading, setLoading] = useState(false);
  const [payLoading, setPayLoading] = useState(false);
  const [order, setOrder] = useState<Order | null>(null);
  const [methods, setMethods] = useState<PaymentMethodItem[]>([]);
  const [selectedMethod, setSelectedMethod] = useState<string>('');
  const [priceDisplay, setPriceDisplay] = useState('');
  const [amount, setAmount] = useState(0);
  const [form] = Form.useForm();
  const [countdown, setCountdown] = useState('');
  const [qrDataUrl, setQrDataUrl] = useState<string>('');
  const pollRef = useRef<NodeJS.Timeout | null>(null);

  // 获取支付方式列表
  useEffect(() => {
    paymentAPI.getPaymentMethods({ lang: language, plan: planCode })
      .then((res) => {
        const data = res.data;
        setMethods(data.methods || []);
        setPriceDisplay(data.price?.display || '');
        setAmount(data.price?.amount || 0);
        if (data.methods?.length > 0) setSelectedMethod(data.methods[0].key);
      })
      .catch(() => message.error(t('common.operationFailed')));
  }, [planCode, language]);

  // 倒计时
  useEffect(() => {
    if (!order?.expires_at) return;
    const timer = setInterval(() => {
      const diff = new Date(order.expires_at!).getTime() - Date.now();
      if (diff <= 0) { setCountdown(language === 'zh-CN' ? '已过期' : 'Expired'); clearInterval(timer); return; }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setCountdown(`${h}h ${m}m ${s}s`);
    }, 1000);
    return () => clearInterval(timer);
  }, [order?.expires_at, language]);

  // 轮询支付状态（支付宝/微信，每3秒查一次）
  const startPolling = useCallback((orderId: number) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const res = await orderAPI.getPublicById(orderId);
        if (res.data.order?.payment_status === 'PAID') {
          clearInterval(pollRef.current!);
          setOrder(res.data.order);
          setStep('done');
          message.success(language === 'zh-CN' ? '支付成功！' : 'Payment successful!');
        }
      } catch { /* ignore */ }
    }, 3000);
  }, [language]);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  // Step 1: 创建订单 → 调支付网关 → 显示二维码或跳转
  const handleCreateOrder = useCallback(async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      // 创建订单
      const res = await orderAPI.createPublic({
        product_id: planCode,
        tool_name: values.tool_name,
        tool_id: toolIdParam ? Number(toolIdParam) : undefined,
        contact_info: values.contact,
        user_contact: values.contact,
        payment_method: selectedMethod,
      });
      const newOrder: Order = res.data.order;
      setOrder(newOrder);

      // 发起支付网关请求
      setPayLoading(true);
      const payRes = await orderAPI.createPayment(newOrder.id, selectedMethod);
      const payData = payRes.data;

      // 根据 payment_type 决定支付行为
      const paymentType = payData.payment_type || 'unknown';

      if (paymentType === 'redirect' && payData.payment_url) {
        // 跳转支付（PayPal / 支付宝兜底模式）
        window.location.href = payData.payment_url;
        return;
      } else if (paymentType === 'qrcode' && payData.qr_url) {
        // 扫码支付（支付宝主要模式 / 微信）
        const dataUrl = await QRCode.toDataURL(payData.qr_url, { width: 220, margin: 2 });
        setQrDataUrl(dataUrl);
        startPolling(newOrder.id);
      } else if (payData.payment_url) {
        // 向后兼容：没有 payment_type 但有 payment_url，直接跳转
        window.location.href = payData.payment_url;
        return;
      } else if (payData.qr_url) {
        // 向后兼容：没有 payment_type 但有 qr_url，渲染二维码
        const dataUrl = await QRCode.toDataURL(payData.qr_url, { width: 220, margin: 2 });
        setQrDataUrl(dataUrl);
        startPolling(newOrder.id);
      }

      setStep('pay');
      message.success(t('checkout.orderCreated'));
    } catch (err: any) {
      if (err?.errorFields) return;
      message.error(err?.response?.data?.message || t('common.operationFailed'));
    } finally {
      setLoading(false);
      setPayLoading(false);
    }
  }, [form, planCode, selectedMethod, toolIdParam, t, startPolling]);

  const stepItems = [
    { title: t('checkout.orderInfo'), icon: <ShoppingCartOutlined /> },
    { title: t('payment.selectMethod'), icon: <QrcodeOutlined /> },
    { title: language === 'zh-CN' ? '完成' : 'Done', icon: <CheckCircleOutlined /> },
  ];
  const currentStepIndex = step === 'form' ? 0 : step === 'pay' ? 1 : 2;

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: '40px 16px' }}>
      <Title level={3} style={{ textAlign: 'center', marginBottom: 32 }}>
        {t('checkout.title')}
      </Title>

      <Steps current={currentStepIndex} items={stepItems} style={{ marginBottom: 32 }} />

      {/* ===== Step 1: 填写信息 + 选择支付方式 ===== */}
      {step === 'form' && (
        <Card>
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <Tag color={planCode === 'premium' ? 'gold' : 'blue'} style={{ fontSize: 16, padding: '4px 16px' }}>
              {language === 'en-US'
                ? (planCode === 'premium' ? 'Homepage Featured' : 'Fast Listing')
                : (planCode === 'premium' ? t('payment.planPremium') : t('payment.planStandard'))}
            </Tag>
            <Title level={3} style={{ margin: '8px 0', color: planCode === 'premium' ? '#faad14' : '#1677ff' }}>
              {priceDisplay || `${amount}`}
            </Title>
            {language === 'en-US' && (
              <Text type="secondary" style={{ fontSize: 13 }}>
                {planCode === 'premium' ? '7-day homepage placement' : 'One-time listing fee'}
              </Text>
            )}
          </div>

          <Form form={form} layout="vertical">
            <Form.Item
              name="tool_name"
              label={t('checkout.toolName')}
              initialValue={toolNameParam || ''}
              rules={[{ required: true, message: t('checkout.toolNamePlaceholder') }]}
            >
              <Input placeholder={t('checkout.toolNamePlaceholder')} maxLength={200} />
            </Form.Item>
            <Form.Item
              name="contact"
              label={t('checkout.contact')}
              rules={[{ required: true, message: t('checkout.contactPlaceholder') }]}
            >
              <Input placeholder={t('checkout.contactPlaceholder')} maxLength={200} />
            </Form.Item>
          </Form>

          <Divider>{t('payment.selectMethod')}</Divider>
          <Row gutter={12}>
            {methods.map((m) => (
              <Col key={m.key} xs={8}>
                <Card
                  hoverable
                  size="small"
                  style={{
                    textAlign: 'center',
                    border: selectedMethod === m.key ? `2px solid ${m.color}` : undefined,
                    cursor: 'pointer',
                  }}
                  onClick={() => setSelectedMethod(m.key)}
                  bodyStyle={{ padding: 12 }}
                >
                  <div style={{ color: m.color, fontWeight: 600 }}>{m.name}</div>
                </Card>
              </Col>
            ))}
          </Row>

          <Button
            type="primary"
            size="large"
            block
            loading={loading || payLoading}
            onClick={handleCreateOrder}
            style={{ marginTop: 24 }}
          >
            {payLoading
              ? (language === 'zh-CN' ? '正在唤起支付...' : 'Connecting payment...')
              : t('checkout.createOrder')}
          </Button>
        </Card>
      )}

      {/* ===== Step 2: 扫码支付 ===== */}
      {step === 'pay' && order && (
        <Card>
          <Alert
            type="info"
            showIcon
            icon={<ClockCircleOutlined />}
            message={t('checkout.expiresIn', { time: countdown })}
            style={{ marginBottom: 16 }}
          />

          <Space direction="vertical" style={{ width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Text type="secondary">{t('checkout.orderNo')}</Text>
              <Text strong copyable={{ tooltips: false }}>{order.order_no}</Text>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Text type="secondary">{t('checkout.amount')}</Text>
              <Text strong style={{ color: '#1677ff', fontSize: 18 }}>{priceDisplay}</Text>
            </div>
          </Space>

          <Divider>
            {selectedMethod === 'alipay' && (language === 'zh-CN' ? '请用支付宝扫码' : 'Scan with Alipay')}
            {selectedMethod === 'wechat' && (language === 'zh-CN' ? '请用微信扫码' : 'Scan with WeChat')}
          </Divider>

          {/* 动态生成的二维码 */}
          {qrDataUrl ? (
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <img src={qrDataUrl} alt="Payment QR Code" width={220} style={{ borderRadius: 8 }} />
              <div style={{ marginTop: 12, color: '#888', fontSize: 13 }}>
                <LoadingOutlined style={{ marginRight: 6 }} />
                {language === 'zh-CN'
                  ? '等待支付确认，扫码后将自动跳转...'
                  : 'Waiting for payment, will auto redirect after payment...'}
              </div>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <Spin size="large" />
            </div>
          )}
        </Card>
      )}

      {/* ===== Step 3: 支付完成 ===== */}
      {step === 'done' && (
        <Result
          status="success"
          title={language === 'zh-CN' ? '支付成功！' : 'Payment Successful!'}
          subTitle={order ? `${t('checkout.orderNo')}: ${order.order_no}` : ''}
          extra={[
            <Button key="orders" type="primary" onClick={() => navigate('/my-orders')}>
              {t('checkout.viewMyOrders')}
            </Button>,
            <Button key="home" onClick={() => navigate('/')}>
              {language === 'zh-CN' ? '返回首页' : 'Back to Home'}
            </Button>,
          ]}
        />
      )}
    </div>
  );
};

export default CheckoutPage;
